import { useMemo, useState } from 'react';
import { Nav } from 'react-bootstrap';
import { SegmentedControlButton } from '@Components/SegmentedControlButton';
import { Size } from '@Components/designConstants';
import { Brand60 } from '@src/utils/colors';
import { DataQaChart, SeriesConfig } from '../components/DataQaChart';
import { IndicatorDot, IndicatorTooltip } from '../components/IndicatorDot';
import { KpiBar, KpiTileData } from '../components/KpiBar';
import { PromoCalendar } from '../components/PromoCalendar';
import { ReviewControls } from '../components/ReviewControls';
import { SectionHeader } from '../components/SectionHeader';
import { SectionStatus, SectionStatusType } from '../data-qa-constants';
import { ContextVarStats, DataQaDataset, MmmConfig, SectionDiffs, WeekRow } from '../data-qa-types';
import { analyzeContextVar, analyzeOutlierFlags, isPromoFlagVar } from '../context-var-utils';
import { filterWeeksToFencepost, modelTypeForProductType } from '../data-qa-utils';
import {
  IndicatorReason,
  reasonsIndicator,
  reasonTexts,
  ReviewIndicator
} from '../media-inputs-utils';

interface ContextVarsSectionProps {
  config: MmmConfig;
  dataset: DataQaDataset;
  diffs: SectionDiffs;
  status: SectionStatusType;
  notes: string;
  pending: boolean;
  isColdStart: boolean;
  onApprove: () => void;
  onFlag: (notes: string) => void;
  onNoteChange: (notes: string) => void;
  // For the Promo Flag's calendar of Offline Inputs promos.
  company: string;
}

const numberFmt = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 });

const TIPS = (
  <>
    <li>
      <strong>Indicators</strong>: red means no data in the modeling range; yellow means the value
      never changes, or the data is cut off by 4+ zero weeks at the start or end of the window;
      green means none of those. Outlier Flags has its own: red when a flag never fires in the
      modeling range, yellow when two flags are active in exactly the same weeks
    </li>
    <li>
      <strong>Time series shape</strong>: Context variables like email blasts are often sporadic —
      zeros with occasional spikes are normal
    </li>
    <li>
      <strong>Data completeness</strong>: Check that the variable has data for the full date range
      and isn&apos;t cut off early or padded with zeros at the edges
    </li>
    <li>
      <strong>Flag</strong> if data looks unreliable, has large unexplained gaps, or doesn&apos;t
      match your expectations
    </li>
    <li>
      <strong>Outlier flags</strong>: one-off calendar-event indicators (Black Friday, Labor Day,
      etc.) are combined into a single chart below — hover a spike to see which flag(s) fired
    </li>
  </>
);

const flattenVar = (weeks: WeekRow[], key: string): WeekRow[] =>
  weeks.map(w => ({ ...w, [key]: w.perContextVar[key] ?? 0 }));

// Away alone declares 15-18 "promo__outlier_*" one-off calendar-event flags (Black
// Friday/Labor Day/Memorial Day windows, etc.) alongside its one real "Promo Flag" --
// almost all zero except a single flagged week each. Reviewing each as its own card
// (KPI tiles + chart) buries the one genuine context variable under a wall of
// near-identical near-empty cards. Split by this substring match: everything else
// keeps the normal per-var treatment; every "outlier" var gets combined into one
// chart (see OutlierFlagsCard) -- Sean's call, optimizing for reviewability over
// showing every declared variable with equal weight.
const isOutlierVar = (key: string): boolean => /outlier/i.test(key);

const OUTLIER_COLOR = Brand60; // same purple ContextVarCard already uses -- one
// series (a count, not per-flag identity -- see below), so no categorical palette
// decision needed.

interface OutlierFlagsCardProps {
  stats: ContextVarStats[];
  weeks: WeekRow[];
  // From analyzeOutlierFlags, listed under the chart.
  reasons: IndicatorReason[];
  bookmarkWeekIso?: string;
  isColdStart: boolean;
}

// One combined chart for every "outlier" context var, rather than one card each.
// Plots a single derived series -- how many outlier flags are active each week --
// instead of one line per flag: with 15+ flags, distinguishing them by color would
// blow well past a legible categorical palette (a handful of hues, not dozens), and
// identity isn't actually the chart's job here -- "when did something fire, and
// what" is. Tooltip identity is carried by renderTooltipExtra instead, which lists
// the specific flag(s) active in the hovered week -- exact vs. "N flags active"
// only papering over which ones.
const OutlierFlagsCard = ({
  stats,
  weeks,
  reasons,
  bookmarkWeekIso,
  isColdStart
}: OutlierFlagsCardProps) => {
  const countedWeeks = useMemo(
    () =>
      weeks.map(w => ({
        ...w,
        outlierCount: stats.reduce(
          (sum, s) => sum + ((w.perContextVar[s.key] ?? 0) !== 0 ? 1 : 0),
          0
        )
      })),
    [weeks, stats]
  );
  const flaggedWeeksCount = useMemo(
    () => countedWeeks.filter(w => w.outlierCount > 0).length,
    [countedWeeks]
  );
  const series: SeriesConfig[] = [
    {
      dataKey: 'outlierCount',
      label: 'Outlier Flags Active',
      color: OUTLIER_COLOR,
      format: 'number'
    }
  ];
  const tiles: KpiTileData[] = [
    {
      label: 'Outlier Weeks Flagged',
      value: `${flaggedWeeksCount} across ${stats.length} flag${stats.length === 1 ? '' : 's'}`
    }
  ];

  return (
    <div className='data-qa-cv-card'>
      <div className='data-qa-cv-card__head'>
        <span className='data-qa-cv-card__title'>Outlier Flags</span>
      </div>
      <div className='data-qa-cv-card__body'>
        <KpiBar tiles={tiles} />
        <DataQaChart
          title='Outlier Flags Over Time'
          weeks={countedWeeks}
          series={series}
          bookmarkWeekIso={bookmarkWeekIso}
          isColdStart={isColdStart}
          height={240}
          hideLegend
          renderTooltipExtra={week => {
            const active = stats.filter(s => (week.perContextVar[s.key] ?? 0) !== 0);
            if (active.length === 0) return undefined;
            return (
              <div className='data-qa-chart__tooltip-extra'>
                {active.map(s => (
                  <div key={s.key} className='data-qa-chart__tooltip-extra-row'>
                    {s.label}
                  </div>
                ))}
              </div>
            );
          }}
        />
        {reasons.length > 0 && (
          <div className='data-qa-cv__reasons'>{reasons.map(r => r.text).join(' · ')}</div>
        )}
      </div>
    </div>
  );
};

const ChartView = { SERIES: 'series', CALENDAR: 'calendar' } as const;
type ChartViewType = (typeof ChartView)[keyof typeof ChartView];

const CHART_VIEW_OPTIONS = [
  { key: ChartView.SERIES, label: 'Time series' },
  { key: ChartView.CALENDAR, label: 'Calendar' }
];

interface ContextVarCardProps {
  stat: ContextVarStats;
  weeks: WeekRow[];
  bookmarkWeekIso?: string;
  isColdStart: boolean;
  // Set for the Promo Flag only: the company and modeling range its calendar covers.
  promoCalendar?: { company: string; start: string; end: string };
}

const ContextVarCard = ({
  stat,
  weeks,
  bookmarkWeekIso,
  isColdStart,
  promoCalendar
}: ContextVarCardProps) => {
  const [view, setView] = useState<ChartViewType>(ChartView.SERIES);
  const varWeeks = useMemo(() => flattenVar(weeks, stat.key), [weeks, stat.key]);
  const series: SeriesConfig[] = [
    { dataKey: stat.key, label: stat.label, color: Brand60, format: 'raw', axis: 'left' }
  ];
  const tiles: KpiTileData[] = [
    { label: 'Avg Weekly', value: numberFmt.format(stat.mean) },
    { label: 'Minimum Weekly', value: numberFmt.format(stat.min) },
    { label: 'Maximum Weekly', value: numberFmt.format(stat.max) }
  ];

  return (
    <div className='data-qa-cv-card'>
      <div className='data-qa-cv-card__head'>
        <span className='data-qa-cv-card__title'>{stat.label}</span>
        {promoCalendar && (
          <SegmentedControlButton<ChartViewType>
            size={Size.SM}
            options={CHART_VIEW_OPTIONS}
            selectedOption={view}
            onChange={setView}
          />
        )}
      </div>
      <div className='data-qa-cv-card__body'>
        {/* The weekly summary describes the series, so it goes with the series view. */}
        {promoCalendar && view === ChartView.CALENDAR ? (
          <PromoCalendar {...promoCalendar} />
        ) : (
          <>
            <KpiBar tiles={tiles} />
            <DataQaChart
              title={`${stat.label} Over Time`}
              weeks={varWeeks}
              series={series}
              bookmarkWeekIso={bookmarkWeekIso}
              isColdStart={isColdStart}
              height={240}
              hideLegend
            />
          </>
        )}
      </div>
    </div>
  );
};

const OUTLIER_PILL = '__outlierFlags';

export const ContextVarsSection = ({
  config,
  dataset,
  diffs,
  status,
  notes,
  pending,
  isColdStart,
  onApprove,
  onFlag,
  onNoteChange,
  company
}: ContextVarsSectionProps) => {
  const modelType = modelTypeForProductType(config.productType);
  const visibleStats = useMemo(
    () => dataset.contextVarStats.filter(s => config.contextVars.includes(s.key)),
    [config.contextVars, dataset.contextVarStats]
  );
  const regularStats = useMemo(
    () => visibleStats.filter(s => !isOutlierVar(s.key)),
    [visibleStats]
  );
  const outlierStats = useMemo(() => visibleStats.filter(s => isOutlierVar(s.key)), [visibleStats]);
  // Bounds the chart x-axis (and the data-completeness readout below) to the model's
  // own training window instead of plotting its entire data history -- same fix already
  // applied to Portfolio/Channels and Dependent Variable.
  const chartWeeks = useMemo(
    () =>
      filterWeeksToFencepost(
        dataset.weeks,
        config.fencepostStart,
        config.fencepostEnd,
        dataset.reviewGranularity
      ),
    [dataset.weeks, config.fencepostStart, config.fencepostEnd, dataset.reviewGranularity]
  );

  // The modeling range, from the config's fencepost (else the weeks charted).
  const rangeStart = config.fencepostStart ?? chartWeeks[0]?.weekIso;
  const rangeEnd = config.fencepostEnd ?? chartWeeks[chartWeeks.length - 1]?.weekIso;
  const promoCalendar =
    rangeStart && rangeEnd ? { company, start: rangeStart, end: rangeEnd } : undefined;

  const analyses = useMemo(
    () => new Map(regularStats.map(stat => [stat.key, analyzeContextVar(chartWeeks, stat.key)])),
    [regularStats, chartWeeks]
  );
  const outlierReasons = useMemo(
    () => analyzeOutlierFlags(chartWeeks, outlierStats),
    [chartWeeks, outlierStats]
  );
  // One chart at a time: a pill per context variable (with its indicator), then one for
  // the combined outlier flags. A pill from another model or stage this one lacks falls
  // back to the first.
  const [activePill, setActivePill] = useState<string | undefined>(undefined);
  const pillKeys = [
    ...regularStats.map(stat => stat.key),
    ...(outlierStats.length > 0 ? [OUTLIER_PILL] : [])
  ];
  const active = activePill && pillKeys.includes(activePill) ? activePill : pillKeys[0];
  const activeStat = regularStats.find(stat => stat.key === active);
  const activeAnalysis = activeStat ? analyses.get(activeStat.key) : undefined;

  const header = (
    <SectionHeader
      title='Context Variable Review'
      modelType={modelType}
      subtitle='Non-marketing variables included in the model'
      tips={TIPS}
    />
  );

  // Reviewed count comes from diffs.summary (this section's own real fingerprint diff),
  // not a per-week flag -- keeps this bar Context-Variables-specific rather than
  // reflecting changes anywhere else in the model. Gated on APPROVED specifically --
  // a flagged section isn't done (Sean, 2026-08-27): "the bar should reflect what's
  // been reviewed AND approved."
  const reviewedWeekCount = status === SectionStatus.APPROVED ? diffs.summary.unchanged : 0;
  const reviewBar = (
    <ReviewControls
      status={status}
      notes={notes}
      pending={pending}
      onApprove={onApprove}
      onFlag={onFlag}
      onNoteChange={onNoteChange}
      approveLabel='Approve Context Vars'
      flagLabel='Flag Context Vars'
      summary={diffs.summary}
      isColdStart={isColdStart}
      reviewed={reviewedWeekCount}
      total={dataset.weeks.length}
      unit='variable-weeks'
    />
  );

  if (visibleStats.length === 0) {
    return (
      <div className='data-qa-cv'>
        {header}
        <div className='data-qa-cv__empty'>
          This model has no context variables — nothing to review.
        </div>
        {reviewBar}
      </div>
    );
  }

  return (
    <div className='data-qa-cv'>
      {header}

      <Nav
        variant='pills'
        className='data-qa-pills'
        activeKey={active}
        onSelect={key => key && setActivePill(key)}
      >
        {regularStats.map(stat => {
          const analysis = analyses.get(stat.key);
          return (
            <Nav.Item key={stat.key}>
              {/* The whole pill carries the dot's hover text, not just the dot. */}
              <IndicatorTooltip
                indicator={analysis?.indicator ?? ReviewIndicator.CLEAR}
                reasons={analysis?.reasons}
                hideWhenClear
              >
                <Nav.Link eventKey={stat.key}>
                  {analysis && (
                    <IndicatorDot
                      indicator={analysis.indicator}
                      reasons={analysis.reasons}
                      tooltip={false}
                    />
                  )}
                  {stat.label}
                </Nav.Link>
              </IndicatorTooltip>
            </Nav.Item>
          );
        })}
        {outlierStats.length > 0 && (
          <Nav.Item>
            <IndicatorTooltip
              indicator={reasonsIndicator(outlierReasons)}
              reasons={reasonTexts(outlierReasons)}
              hideWhenClear
            >
              <Nav.Link eventKey={OUTLIER_PILL}>
                <IndicatorDot
                  indicator={reasonsIndicator(outlierReasons)}
                  reasons={reasonTexts(outlierReasons)}
                  tooltip={false}
                />
                Outlier Flags
              </Nav.Link>
            </IndicatorTooltip>
          </Nav.Item>
        )}
      </Nav>

      {active === OUTLIER_PILL && (
        <OutlierFlagsCard
          stats={outlierStats}
          weeks={chartWeeks}
          reasons={outlierReasons}
          bookmarkWeekIso={diffs.bookmarkWeekIso}
          isColdStart={isColdStart}
        />
      )}
      {activeStat && activeAnalysis && (
        <>
          <ContextVarCard
            key={activeStat.key}
            stat={activeStat}
            weeks={chartWeeks}
            bookmarkWeekIso={diffs.bookmarkWeekIso}
            isColdStart={isColdStart}
            promoCalendar={isPromoFlagVar(activeStat.key) ? promoCalendar : undefined}
          />
          <div className='data-qa-cv__completeness'>
            <strong>Data completeness — {activeStat.label}:</strong>{' '}
            {activeAnalysis.completeness.zero} of {activeAnalysis.completeness.total} weeks are zero
            ({activeAnalysis.completeness.pct}%) | Non-zero data spans{' '}
            <strong>{activeAnalysis.completeness.span}</strong>
          </div>
          {activeAnalysis.reasons.length > 0 && (
            <div className='data-qa-cv__reasons'>{activeAnalysis.reasons.join(' · ')}</div>
          )}
        </>
      )}
      {reviewBar}
    </div>
  );
};
